// Portal Phase C — Work tasks module: the shared task model.
// ---------------------------------------------------------------------------
// Maps + helpers used across every view (list, board, detail, projects). This
// is the single place that knows the enum vocabularies (area/status/priority/
// gate/source) and how to humanize, color-key, sort, group, and resolve
// assignees. Colors are referenced by CSS-variable NAME only — never hardcoded
// hex in JS — so the module recolors in Default / Comfort / Comfort-Dark. The
// area + status identity hues live as portal-scoped --area-* / --status-* vars
// in WorkTasks.css (the Phase B --space-* precedent), not the brand palette.
//
// DERIVED FROM CANON (Slice 1a): the enum vocabularies below are now built from
// `src/canon/taxonomy.json` (the master) so there is one source. This module is
// a thin adapter that shapes the canon into the exact export names/shapes every
// view already consumes — do not hand-edit enum values here; edit taxonomy.json
// and re-publish. Colors are NOT canon (they are CSS vars), so the color-var
// helpers stay literal. The JSON import is BARE (no `with { type: 'json' }`): this
// module is consumed ONLY by CRA/webpack, whose build accepts the bare form but
// rejects the import attribute (babel re-emits it in a legacy syntax webpack can't
// parse). taskAssignees.js documents the full webpack-vs-Node attribute story.

import taxonomy from '../../../canon/taxonomy.json';
import people from '../../../canon/people.json';
/* One-way: spaceFields imports nothing, so there is no cycle here. The private
   area vocabulary lives there (with the policy it belongs to) and is resolved
   here (with every other label). */
import { PRIVATE_AREAS } from './spaceFields';

// uid -> person identity (initials, first name, avatar var), derived from the
// canon roster (src/canon/people.json) so every canonical person — not just a
// hardcoded pair — resolves to a real chip. First name comes from displayName;
// initials are the first letter of the first name, extended to two letters when
// an earlier-ordered person already claimed that letter (canon order: Grace 'G',
// Anthony 'A', Annelise 'An' — matching portalSpaces.js). Color vars follow the
// --person-<canon key> convention (WorkTasks.css).
export const PEOPLE_BY_UID = (() => {
	const roster = [...(people.people || [])].sort(
		(a, b) => (a.order || 0) - (b.order || 0)
	);
	const takenInitials = new Set();
	const byUid = {};
	for (const person of roster) {
		const firstName = String(person.displayName || '').trim().split(/\s+/)[0] || '?';
		let initials = firstName.slice(0, 1);
		if (takenInitials.has(initials)) initials = firstName.slice(0, 2);
		takenInitials.add(initials);
		byUid[person.authUid] = {
			uid: person.authUid,
			initials,
			name: firstName,
			colorVar: `--person-${person.key}`,
			// Carried through from canon so a surface can ask whether someone may
			// be OFFERED a task, not merely rendered. The owner-walled private
			// spaces need it (see spaceFields.isPersonOffered); everything that
			// only draws an avatar ignores it.
			owner: person.owner === true,
		};
	}
	return byUid;
})();

// canon person KEY -> the same identity shape. The triage classifier proposes an
// assignee by canon key ('grace' / 'anthony' / 'annelise'), not by UID, so the
// proposal surface (and the approve path that commits assigneeUids) needs this
// direction as well as PEOPLE_BY_UID.
export const PEOPLE_BY_KEY = Object.fromEntries(
	(people.people || [])
		.filter((p) => p.authUid && PEOPLE_BY_UID[p.authUid])
		.map((p) => [p.key, PEOPLE_BY_UID[p.authUid]])
);

// Resolve the canonical assigneeUids to ONE chip per person. Unknown UIDs get a
// neutral fallback chip (initial from the legacy name mirror if we can find it)
// so an assignee never silently vanishes. We deliberately never read assignedTo
// for rendering — that legacy name mirror is what double-rendered Anthony.
export function resolveAssignees(task) {
	const uids = Array.isArray(task?.assigneeUids) ? task.assigneeUids : [];
	const seen = new Set();
	const people = [];
	for (const uid of uids) {
		if (!uid || seen.has(uid)) continue;
		seen.add(uid);
		const known = PEOPLE_BY_UID[uid];
		if (known) {
			people.push(known);
		} else {
			people.push({
				uid,
				initials: '?',
				name: 'Teammate',
				colorVar: '--person-annelise',
			});
		}
	}
	return people;
}

// ---- Area (identity: left accent bar + tag) --------------------------------
// Derived from canon taxonomy.areas (order carries the display sequence).
const AREAS_BY_ORDER = [...taxonomy.areas].sort((a, b) => a.order - b.order);

/** The STUDIO areas, in canon order. Unchanged: this is what Work offers, and
    nothing private is ever appended to it. `areaOrderFor` picks the right list
    for a space; only Work resolves to this one. */
export const AREA_ORDER = AREAS_BY_ORDER.map((a) => a.key);

/* RESOLUTION IS GLOBAL, OFFERING IS PER SPACE — the same split `spaceFields`
   applies to people, running the other direction. Every area key that any space
   can offer must be resolvable HERE, or a private task renders its area as "No
   area" in neutral grey: selectable in the picker and unlabelled everywhere
   else (chip, row, board group header, aria-label). Merging the private
   vocabularies in costs nothing — a space can only ever OFFER its own list, so
   Work's picker never sees "Home" no matter what this map contains. */
export const AREAS = Object.fromEntries([
	...AREAS_BY_ORDER.map((a) => [a.key, { key: a.key, label: a.label }]),
	...Object.values(PRIVATE_AREAS)
		.flat()
		.map((a) => [a.key, { key: a.key, label: a.label }]),
]);

export function areaMeta(area) {
	return AREAS[area] || { key: 'none', label: 'No area' };
}

/**
 * The area keys a space offers, in display order.
 *
 * NOT a filter over AREA_ORDER, and that is the point — a private space's areas
 * are a SUBSTITUTION, not a subtraction (see the long note in spaceFields.js).
 * Filtering the studio seven could only ever yield studio keys.
 *
 * @param {object} fields from `spaceFields(space)`
 * @returns {string[]} area keys; the studio order when the space offers all of
 *   canon (`areas: null`), which is Work and any component mounted outside a
 *   space.
 */
export function areaOrderFor(fields) {
	if (!fields || !fields.areas) return AREA_ORDER;
	return fields.areas.map((a) => a.key);
}

// CSS var names for an area's accent / tint / text-on-tint. 'none' falls back
// to neutral line/muted tokens so an unset area still renders cleanly.
export function areaVars(area) {
	if (!area || !AREAS[area]) {
		return {
			accent: '--cyv-line-cool',
			tint: '--surface-page',
			text: '--text-muted',
		};
	}
	return {
		accent: `--area-${area}`,
		tint: `--area-${area}-tint`,
		text: `--area-${area}-text`,
	};
}

// ---- Status ----------------------------------------------------------------
const STATUSES_BY_ORDER = [...taxonomy.statuses].sort((a, b) => a.order - b.order);

export const STATUS_ORDER = STATUSES_BY_ORDER.map((s) => s.key);

export const STATUS_META = Object.fromEntries(
	STATUSES_BY_ORDER.map((s) => [s.key, { key: s.key, label: s.label }])
);

// Tap-to-advance cycle on a row's status circle.
export function nextStatus(status) {
	if (status === 'pending') return 'in_progress';
	if (status === 'in_progress') return 'completed';
	return 'pending';
}

export function isOpen(task) {
	return task?.status === 'pending' || task?.status === 'in_progress';
}

// The counterpart to isOpen(). Added 2026-08-26 with the global show-completed
// preference: closed-ness was written inline as `status === 'completed'` in
// about ten places, which is exactly how the board and the list views came to
// disagree about it before needsTriage/needsInfo were moved in here. One
// predicate, one answer.
//
// NOT simply `!isOpen(task)` — a task with a missing or unrecognised status is
// neither open nor completed, and must not be hidden by a "hide completed"
// filter. Hiding a row nobody chose to close is how work disappears silently.
export function isCompleted(task) {
	return task?.status === 'completed';
}

// Apply the viewer's show-completed preference to a list of tasks.
//
// The one place that decision is expressed, so every surface that renders a
// list — nested children, the project rollup, the mention picker — hides the
// same rows for the same reason. Returns the array unchanged when completed
// are shown, so callers can use it unconditionally.
export function applyShowCompleted(tasks, showCompleted) {
	if (showCompleted) return tasks || [];
	return (tasks || []).filter((task) => !isCompleted(task));
}

// "Needs triage" — a raw capture still waiting to be filed. BOTH halves matter.
//
// `triaged !== false` — undefined on docs predating the field, and the backfill
// stamped those true, so a missing flag reads as triaged rather than as needing
// attention.
//
// `isOpen()` — a CLOSED task never needs triage, whatever its flag says. This is
// not a defensive extra: closed-but-untriaged accumulates continuously, because
// closing a task writes `status` and nothing else (the MCP `complete_task` tool
// is deliberately status-only, byte-equivalent to the portal UI close). Any task
// closed without having been triaged keeps `triaged: false` FOREVER. Measured
// 2026-07-27: 33 of 85 such tasks in prod, every one of them finished work.
//
// Lives here rather than inside the module's filter closure because it is a
// domain question, not view state — and because the two views disagreed while
// it was view state: the list views filter on isOpen() already, the board
// deliberately does not (so its Completed column can populate), so the same chip
// answered differently depending on where you stood.
export function needsTriage(task) {
	return task?.triaged === false && isOpen(task);
}

// "Needs info" — a filed task carrying a question only Anthony can answer. Same
// two-halves shape as needsTriage() above, and it lives here for the same
// reason: it is a domain question, not view state.
//
// `needsInfo === true` — the raw flag, written by the triage helper when it
// can't responsibly resolve something (never by the client; there is no writer
// in src/).
//
// `isOpen()` — and this half is why the predicate exists at all. NOTHING clears
// `needsInfo`: answering the question is a comment, and `add_comment` is
// append-only while `complete_task` is status-only, so an answered-and-closed
// card keeps the flag forever. That is the identical mechanism that stranded 33
// closed cards behind `triaged: false`, arriving by a different route. Measured
// 2026-07-28: 17 docs carry `needsInfo: true`, 11 open — so 6 finished cards
// were showing under the chip in the board view, whose Completed column
// deliberately skips isOpen().
//
// Note the deliberate near-collision: `needsInfo(task)` is the question worth
// asking, `task.needsInfo` is only half of its answer. Filter on the function.
export function needsInfo(task) {
	return task?.needsInfo === true && isOpen(task);
}

// ---- Priority (quiet) ------------------------------------------------------
export const PRIORITY_ORDER = [...taxonomy.priorities.values]
	.sort((a, b) => a.order - b.order)
	.map((p) => p.key);

export const PRIORITY_WEIGHT = Object.fromEntries(PRIORITY_ORDER.map((k, i) => [k, i]));

// ---- Gate (humanized + filterable) -----------------------------------------
const GATES_BY_ORDER = [...taxonomy.gates].sort((a, b) => a.order - b.order);

export const GATE_META = Object.fromEntries(
	GATES_BY_ORDER.map((g) => [g.key, { key: g.key, label: g.label, icon: g.icon }])
);

export const GATE_ORDER = GATES_BY_ORDER.map((g) => g.key);

export function gateMeta(gate) {
	return GATE_META[gate] || null;
}

// ---- Source (quiet provenance icon, set automatically) ---------------------
export const SOURCE_META = Object.fromEntries(
	taxonomy.sources.map((s) => [s.key, { key: s.key, label: s.label, icon: s.icon }])
);

export function sourceMeta(source) {
	return SOURCE_META[source] || SOURCE_META.manual;
}

// ---- Comment @mentions (the shared rail — people + the @claude flag) --------
// Comments' mentions[] stores name strings: a person's canon first name, or the
// literal 'claude' (the agent flag target — 2026-07-13 handoff D5, folded into
// the @mentions card). Extraction is deterministic and case-insensitive; tokens
// that aren't on the rail are ignored, never stored. Mirrored server-side in
// mcp/src/core.extractMentions — keep the two in sync.
export const MENTION_BY_TOKEN = (() => {
	const map = { claude: 'claude' };
	for (const p of people.people || []) {
		const first = String(p.displayName || '').trim().split(/\s+/)[0];
		if (first) map[first.toLowerCase()] = first;
	}
	return map;
})();

export function extractMentions(text) {
	const out = [];
	const seen = new Set();
	for (const match of String(text || '').matchAll(/@([a-zA-Z]+)/g)) {
		const stored = MENTION_BY_TOKEN[match[1].toLowerCase()];
		if (stored && !seen.has(stored)) {
			seen.add(stored);
			out.push(stored);
		}
	}
	return out;
}

// An @claude mention is UNRESOLVED until an agent-authored comment (source
// 'claude') appears after it in the same thread — append-only resolution, no
// mutable boolean. `comments` must be createdAt-ascending (the order
// subscribeToTaskComments already delivers).
export function unresolvedClaudeMentions(comments) {
	const list = Array.isArray(comments) ? comments : [];
	let lastAgentReply = -1;
	list.forEach((c, i) => {
		if (c.source === 'claude') lastAgentReply = i;
	});
	return list.filter(
		(c, i) =>
			i > lastAgentReply &&
			(Array.isArray(c.mentions) ? c.mentions : []).some(
				(m) => String(m).toLowerCase() === 'claude'
			)
	);
}

// ---- Due / date helpers ----------------------------------------------------
// Tasks come off TaskService with deadline/closedAt already coerced to Date|null.
function startOfDay(d) {
	const x = new Date(d);
	x.setHours(0, 0, 0, 0);
	return x;
}

// Returns { state: 'overdue'|'today'|'soon'|'upcoming', label, icon } | null.
// `now` is injected so callers share one clock (and tests stay deterministic).
export function dueInfo(deadline, now = new Date()) {
	if (!deadline) return null;
	const due = startOfDay(deadline);
	const today = startOfDay(now);
	const dayMs = 86400000;
	const diffDays = Math.round((due - today) / dayMs);
	const label = formatShortDate(deadline);
	if (diffDays < 0) {
		return { state: 'overdue', label, icon: 'fa-circle-exclamation', diffDays };
	}
	if (diffDays === 0) {
		return { state: 'today', label: 'Today', icon: 'fa-calendar-day', diffDays };
	}
	if (diffDays <= 7) {
		return { state: 'soon', label, icon: 'fa-calendar', diffDays };
	}
	return { state: 'upcoming', label, icon: 'fa-calendar', diffDays };
}

// Which urgency section a task belongs in (TaskListView's Needs action / Today).
// A GATE OUTRANKS THE DEADLINE: a gated task is parked, and the urgency ramp is
// about what to pick up next, so a date nobody intends to act on must not place
// it. Without this an undated parked card lands in No date (which opens by
// default) beside real work, and a deploy-gated one can read as Overdue. This is
// the same `parked = !!task.gate` rule that already swaps the due label for the
// gate badge (TaskCard, TaskRow) and already sinks gated work within a bucket
// (sortNeedsAction below) — applied to placement.
export function dueBucket(task, now = new Date()) {
	if (task?.gate) return 'parked';
	const info = dueInfo(task?.deadline, now);
	if (!info) return 'none';
	if (info.state === 'overdue') return 'overdue';
	if (info.state === 'today') return 'today';
	if (info.diffDays <= 7) return 'week';
	return 'later';
}

// Which column a task belongs in on the board's Status grouping. Precedence is
// COMPLETED > GATE > STATUS, and each step earns its place:
//   completed first — a finished task carrying a stale gate (several exist in
//     prod) is done, not parked. Without this they would desert the Completed
//     column, which reads as work reopening itself.
//   gate next — the same rule dueBucket applies to the urgency ramp. A parked
//     card must live in exactly ONE column, so it leaves Pending / In progress
//     rather than appearing in both.
// Returns a column key, so the caller never re-derives the precedence.
export function boardStatusColumn(task) {
	if (task?.status === 'completed') return 'completed';
	if (task?.gate) return 'parked';
	return task?.status || 'pending';
}

// What a drop on the Status board should DO. Returns an explicit action rather
// than an update object, because parking is not a write — it is a question.
// Pure, so the rule is testable away from the pointer plumbing.
//
//   into Parked  → 'choose-gate'. Every gate value names WHAT the card is
//                  waiting on (a deploy, a decision, Cowork), and a drag cannot
//                  imply which. Guessing one would put unlabelled cards in the
//                  lane, and the lane is only worth having if "parked" reliably
//                  means "blocked on something" — that is what lets an agent
//                  reading the queue defer it, and what lets a human trust it.
//                  So the drop asks; the caller writes whatever comes back.
//   out of Parked → write the status AND clear the gate in the same update. The
//                  gate outranks status for membership, so without the clear the
//                  card would take the new status and stay in Parked — looking
//                  like the drag silently failed.
//   completed → Parked is refused. Completed outranks the gate, so the card
//                  would not move; writing a gate anyway is a silent write with
//                  no visible effect.
export function statusDrop(task, targetKey) {
	if (targetKey === 'parked') {
		if (task?.status === 'completed') return { action: 'none' };
		if (task?.gate) return { action: 'none' };
		return { action: 'choose-gate' };
	}
	if (task?.status === targetKey && !task?.gate) return { action: 'none' };
	return {
		action: 'write',
		update: { status: targetKey, ...(task?.gate ? { gate: null } : {}) },
	};
}

const MONTHS = [
	'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
	'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export function formatShortDate(d) {
	if (!d) return '';
	const date = d instanceof Date ? d : new Date(d);
	if (Number.isNaN(date.getTime())) return '';
	return `${MONTHS[date.getMonth()]} ${date.getDate()}`;
}

// ---- Quick due-date setting (the row-level date popover) --------------------
// Every deadline this app writes is a LOCAL start-of-day Date, because that is
// what dueInfo() compares against (it startOfDay's both sides). The helpers
// below all funnel through localDay so nothing can write a mid-day timestamp
// that renders as one day here and another day for a reader an hour away.

/** A local midnight Date, offset by `addDays` from the given day. */
export function localDay(from, addDays = 0) {
	const d = new Date(from);
	d.setHours(0, 0, 0, 0);
	d.setDate(d.getDate() + addDays);
	return d;
}

// THE TRAP THIS EXISTS TO AVOID: `new Date('2026-08-14')` is parsed by spec as
// UTC midnight, which in every US timezone is the EVENING OF AUG 13 locally —
// so a native <input type="date"> round-tripped through the Date constructor
// silently lands a day early. Splitting the parts and using the local
// constructor is the fix, and it is why this is a named, tested function rather
// than an inline `new Date(value)` at the call site.
export function parseDateInput(value) {
	if (!value) return null;
	const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
	if (!m) return null;
	const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
	d.setHours(0, 0, 0, 0);
	return Number.isNaN(d.getTime()) ? null : d;
}

/** The inverse: a Date → the `YYYY-MM-DD` a date input expects, in LOCAL terms. */
export function toDateInputValue(d) {
	if (!d) return '';
	const date = d instanceof Date ? d : new Date(d);
	if (Number.isNaN(date.getTime())) return '';
	const pad = (n) => String(n).padStart(2, '0');
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// The quick presets, in the order they appear in the popover. Modeled on Apple
// Reminders: the four dates people actually pick, so the common case is one tap
// and the date picker is the escape hatch rather than the entry point.
//   • This weekend → the coming Saturday. ON a Saturday it means TODAY (the
//     weekend you are in), and on a Sunday it means NEXT Saturday — offering
//     "this weekend" as a date already past would be the one useless answer.
//   • Next week → the coming Monday, always strictly in the future.
export function quickDeadlineOptions(now = new Date()) {
	const today = localDay(now);
	const dow = today.getDay(); // 0 Sun … 6 Sat
	const toSaturday = dow === 6 ? 0 : (6 - dow + 7) % 7;
	const toMonday = ((1 - dow + 7) % 7) || 7;
	return [
		{ key: 'today', label: 'Today', date: today },
		{ key: 'tomorrow', label: 'Tomorrow', date: localDay(today, 1) },
		{
			key: 'weekend',
			label: 'This weekend',
			date: localDay(today, toSaturday),
		},
		{ key: 'nextweek', label: 'Next week', date: localDay(today, toMonday) },
	].map((o) => ({ ...o, hint: formatShortDate(o.date) }));
}

/** True when `deadline` already falls on the same local day as `date`. */
export function isSameDay(deadline, date) {
	if (!deadline || !date) return false;
	return localDay(deadline).getTime() === localDay(date).getTime();
}

// ---- Sorting + grouping ----------------------------------------------------
// Needs Action order: priority (high→low) then due date (soonest first; no-due
// last), then title. Parked (gated) work sinks below active within the bucket
// so live work surfaces first.
export function sortNeedsAction(tasks, now = new Date()) {
	return [...tasks].sort((a, b) => {
		const ag = a.gate ? 1 : 0;
		const bg = b.gate ? 1 : 0;
		if (ag !== bg) return ag - bg;
		const ap = PRIORITY_WEIGHT[a.priority] ?? 1;
		const bp = PRIORITY_WEIGHT[b.priority] ?? 1;
		if (ap !== bp) return ap - bp;
		const ad = a.deadline ? startOfDay(a.deadline).getTime() : Infinity;
		const bd = b.deadline ? startOfDay(b.deadline).getTime() : Infinity;
		if (ad !== bd) return ad - bd;
		return (a.title || '').localeCompare(b.title || '');
	});
}

// ---- Task-list sort control -------------------------------------------------
// Explicit named options (not a key+direction toggle) — clearer for people
// still learning the system than a column-header click. Client-side sort of
// the already-loaded array only; never wire this to a Firestore orderBy (that
// would need new composite indexes we don't want to gate on).
export const SORT_OPTIONS = [
	{ key: 'addedNewest', label: 'Date added — newest first' },
	{ key: 'addedOldest', label: 'Date added — oldest first' },
	{ key: 'titleAsc', label: 'Title A–Z' },
	{ key: 'titleDesc', label: 'Title Z–A' },
	{ key: 'priorityHigh', label: 'Priority (High → Low)' },
	{ key: 'dueSoonest', label: 'Due date (soonest first)' },
	// OPT-IN by canon (core.md → Capture reality, 2026-08-21): activity must
	// never be the DEFAULT sort — reordering the board on read is self-erasing
	// and destroys the record that anything was new. As a chosen mode it is
	// nearly free once lastActivityAt exists.
	{ key: 'activityRecent', label: 'Recent activity first' },
];

export const DEFAULT_SORT = 'addedNewest';

// Stable tiebreaker for every mode below so ties never jitter between renders.
function byTitle(a, b) {
	return (a.title || '').localeCompare(b.title || '');
}

// Nulls always sort last, regardless of direction.
const SORT_COMPARATORS = {
	addedNewest: (a, b) => {
		const at = a.createdAt ? a.createdAt.getTime() : -Infinity;
		const bt = b.createdAt ? b.createdAt.getTime() : -Infinity;
		return bt - at || byTitle(a, b);
	},
	addedOldest: (a, b) => {
		const at = a.createdAt ? a.createdAt.getTime() : Infinity;
		const bt = b.createdAt ? b.createdAt.getTime() : Infinity;
		return at - bt || byTitle(a, b);
	},
	titleAsc: byTitle,
	titleDesc: (a, b) => byTitle(b, a),
	priorityHigh: (a, b) => {
		const ap = PRIORITY_WEIGHT[a.priority] ?? Infinity;
		const bp = PRIORITY_WEIGHT[b.priority] ?? Infinity;
		return ap - bp || byTitle(a, b);
	},
	dueSoonest: (a, b) => {
		const ad = a.deadline ? startOfDay(a.deadline).getTime() : Infinity;
		const bd = b.deadline ? startOfDay(b.deadline).getTime() : Infinity;
		return ad - bd || byTitle(a, b);
	},
	activityRecent: (a, b) => {
		// lastActivityAt is trigger-owned and null on docs the triggers haven't
		// touched; updatedAt is the honest fallback clock for those.
		const at = (a.lastActivityAt || a.updatedAt)?.getTime?.() ?? -Infinity;
		const bt = (b.lastActivityAt || b.updatedAt)?.getTime?.() ?? -Infinity;
		return bt - at || byTitle(a, b);
	},
};

export function sortTasks(tasks, sortKey = DEFAULT_SORT) {
	const cmp = SORT_COMPARATORS[sortKey] || SORT_COMPARATORS[DEFAULT_SORT];
	return [...tasks].sort(cmp);
}

// Split a flat task list into top-level tasks + a parentId->children[] map.
// Sub-tasks are top-level teacherTasks docs carrying parentId (schema-delta §3),
// so the tree is reconstructed here, not stored.
export function buildTaskTree(tasks) {
	const byId = new Map(tasks.map((t) => [t.id, t]));
	const childrenByParent = new Map();
	const roots = [];
	for (const t of tasks) {
		const pid = t.parentId;
		if (pid && byId.has(pid)) {
			if (!childrenByParent.has(pid)) childrenByParent.set(pid, []);
			childrenByParent.get(pid).push(t);
		} else {
			roots.push(t);
		}
	}
	return { roots, childrenByParent, byId };
}

export function childProgress(children) {
	const total = children?.length || 0;
	const done = (children || []).filter((c) => c.status === 'completed').length;
	return { done, total, pct: total ? Math.round((done / total) * 100) : 0 };
}

// Generic "group tasks by key" → ordered [{ key, label, tasks }].
export function groupBy(tasks, keyFn, order, labelFn) {
	const map = new Map();
	for (const t of tasks) {
		const k = keyFn(t) ?? 'none';
		if (!map.has(k)) map.set(k, []);
		map.get(k).push(t);
	}
	const keys = order
		? order.filter((k) => map.has(k)).concat(
				[...map.keys()].filter((k) => !order.includes(k))
		  )
		: [...map.keys()];
	return keys.map((k) => ({
		key: k,
		label: labelFn ? labelFn(k) : k,
		tasks: map.get(k),
	}));
}
